// contexts/ThemeContext.js
'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

// ─── Constants ─────────────────────────────────────────────────────────────────
const STORAGE_KEY  = 'firesync-theme';
const VALID_THEMES = ['light', 'dark', 'system'];
const DEFAULT      = 'system';

// ─── Context ───────────────────────────────────────────────────────────────────
const ThemeContext = createContext({
    theme:        DEFAULT,   // stored preference: 'light' | 'dark' | 'system'
    resolvedTheme: 'light',  // actual applied theme: 'light' | 'dark'
    setTheme:     () => {},
    toggleTheme:  () => {},
});

// ─── Provider ──────────────────────────────────────────────────────────────────
export function ThemeProvider({ children }) {
    // Initialise from localStorage (set by the anti-FOUC inline script in layout.js)
    const [theme, setThemeState] = useState(() => {
        if (typeof window === 'undefined') return DEFAULT;
        const stored = localStorage.getItem(STORAGE_KEY);
        return VALID_THEMES.includes(stored) ? stored : DEFAULT;
    });

    const [resolvedTheme, setResolvedTheme] = useState('light');

    // ── Apply theme to <html> ──────────────────────────────────────────────────
    const applyTheme = useCallback((preference) => {
        const root = document.documentElement;

        let resolved;
        if (preference === 'system') {
            resolved = window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        } else {
            resolved = preference;
        }

        // Set data-theme on <html> — CSS variables react to this
        root.setAttribute('data-theme', resolved);
        setResolvedTheme(resolved);
    }, []);

    // ── Persist & apply when preference changes ────────────────────────────────
    const setTheme = useCallback((newTheme) => {
        if (!VALID_THEMES.includes(newTheme)) return;
        localStorage.setItem(STORAGE_KEY, newTheme);
        setThemeState(newTheme);
        applyTheme(newTheme);
    }, [applyTheme]);

    // ── Toggle light ↔ dark (skips system) ────────────────────────────────────
    const toggleTheme = useCallback(() => {
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    }, [resolvedTheme, setTheme]);

    // ── On mount: apply stored preference ─────────────────────────────────────
    useEffect(() => {
        applyTheme(theme);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Listen for OS preference changes when theme === 'system' ──────────────
    useEffect(() => {
        if (theme !== 'system') return;

        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => applyTheme('system');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [theme, applyTheme]);

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────
export function useTheme() {
    return useContext(ThemeContext);
}